import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import TableComponent from '../../../components/Table/TableComponent';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';

import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import { TableCell, TableRow } from '@mui/material';
import { SearchIcon } from '../../../constants/icons';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import ChartButton from '../../../components/Button/ChartButton';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import ClearIcon from "@mui/icons-material/Clear";
import Chart from "../../../components/Chart";
import { Hematocrit, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface';

const HematocritForm: React.FC<{ patientId: string }> = ({ patientId }) => {
    const [hematocritRecords, setHematocritRecords] = useState<Hematocrit[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [editRecord, setEditRecord] = useState<Hematocrit | null>(null);
    const [showChart, setShowChart] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
        startDate: '',
        endDate: '',
    });
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [recordToDelete, setRecordToDelete] = useState<Hematocrit | null>(null)
    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    useEffect(() => {
        fetchHematocritRecords(dateRange.startDate, dateRange.endDate);
    }, [page, rowsPerPage, searchTerm]); // Removed dateRange from dependencies

    const fetchHematocritRecords = async (startDate: string = '', endDate: string = '') => {
        setHematocritRecords([])
        setLoading(true);
        const authToken = sessionStorage.getItem('authToken');
        try {
            const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {
                params: {
                    limit: rowsPerPage,
                    page: page + 1,
                    from: startDate,
                    to: endDate,
                    order_by: 'details->date',
                    dir: 2,
                    user_id: patientId,
                    slug: 'hct',
                    searchkey: searchTerm || undefined,
                },
                headers: getAxiosConfig().headers,
            });

            const { vitals } = response.data.data
            const total = response.data.data.pagination!.total

            if (Array.isArray(vitals) && vitals.length > 0) {
                const formattedData: Hematocrit[] = vitals.map((vital) => ({
                    id: vital.id,
                    user_id: vital.user_id || 0, // Assuming user_id is in the response
                    consult_id: vital.consult_id || null,
                    peripheral_id: vital.peripheral_id || null,
                    slug: vital.slug || 'respiration',
                    details: {
                      date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
                        time: vital.details.time,
                        value: vital.details.hct,
                        hctFlag: vital.details.hctFlag, // Ensure the correct property name
                        range_code: vital.details.range_code, // Ensure you have this property if needed
                        hct: vital.details.hct,
                    },
                    freeze: vital.freeze,
                    document: vital.document || null,

                }));
                setHematocritRecords(formattedData)
                setTotalRecords(total)
            }


        } catch (error) {
            console.error('Failed to fetch data.');
        } finally {
            setLoading(false);
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('')
        setDateRange({ startDate: '', endDate: '' })
        setPage(0)
        setRowsPerPage(5)
        fetchHematocritRecords()
        setFiltersApplied(false);
    }

  

    const renderRow = (item: Hematocrit, index: number) => (
        <TableRow key={item.id}>
            <TableCell  className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{page * rowsPerPage + index + 1}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{(item.details.date)}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.time}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.hct}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
                <span
                    style={{
                        backgroundColor: item.details.range_code,
                        padding: '4px 8px',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                    }}
                >
                    {item.details.hctFlag}
                </span>
            </TableCell>
        </TableRow>
    );

    const chartData = {
        labels: hematocritRecords.map(record => record.details.date),
        datasets: [
            {
                label: 'Hematocrit (%)',
                data: hematocritRecords.map(record => record.details.hct),
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderWidth: 1,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            tooltip: {
                callbacks: {
                    label: (context: any) => `${context.dataset.label}: ${context.raw} %`,
                },
            },
        },
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between w-full gap-4 pb-4">
                <div className="flex flex-grow space-x-2 gap-2">
                    <DateRangePicker
                        startDateId="Hematocrit_Start_Date"
                        endDateId="Hematocrit_End_Date"
                        onChange={(dates: { startDate: string; endDate: string }) => {
                            setDateRange(dates);
                            setFiltersApplied(false);
                        }}
                        value={dateRange} // Update value prop to use dateRange state
                    />
                       <IconButton
              icon={<SearchIcon />}
              onClick={() => {
                fetchHematocritRecords(dateRange.startDate, dateRange.endDate);
                setFiltersApplied(true); // Mark filters as applied when search is clicked
              }}
            />

            {filtersApplied && (
              <IconButton
                icon={<ClearIcon />}
                onClick={handleClearFilters}
              />
            )}          
                        </div>
                
                <div className="flex space-x-4">
                    <ChartButton
                        text={showChart ? 'List' : 'Chart'}
                        onClick={() => setShowChart(prev => !prev)}
                    />
                   </div>
            </div>
            {loading ? (
        <div className="flex justify-center items-center h-64">
       
        </div>
      ) : showChart ? (
        <Chart data={chartData} options={chartOptions} />
      ) : (
        <>
                    <TableComponent
                        headers={['S.No', 'Date', 'Time', 'Value (%)', 'Result']}
                        data={hematocritRecords}
                        renderRow={renderRow} error={''} />
                   
                  </>
                )}
            <TablePaginationComponent
                count={totalRecords}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, newPage) => {
                    setPage(newPage);
                }}
                onRowsPerPageChange={(event) => {
                    const newRowsPerPage = parseInt(event.target.value, 10);
                    setRowsPerPage(newRowsPerPage);
                    setPage(0);
                }}
            />
       

        </div>
    );
};

export default HematocritForm;
